import React, { useState, useEffect } from "react";
import { UserContext } from "./userContext";
import {
    DOCUSIGN_EVENT,
    DOCUSIGN_SIGNING_COMPLETE,
    DOCUSIGN_DECLINE,
} from "../utils/constants";
import {
    buildPathWithIncentiveId,
    getIncentiveIdFromQuery,
} from "../helpers/incentiveDeepLinkHelper";
import { useNavigate } from "react-router-dom";
import * as rewardsApi from "../services/ApiServices/ApiService";
import { logTrace } from "../services/ApiServices/LoggingService";
import { markGtmSessionUnavailable, setGtmSession } from "../services/gtm/gtmTracker";

const UserContextProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    const getParticipationChangeFlag = async () => {
        const handleError = (error) => {
            logTrace(error, "error when calling rewardsApi.getParticipationChangeFlag", "getParticipationChangeFlag in UserContextProvider.jsx");
            return null;
        };

        const handleSuccess = (responseData) => {
            return responseData;
        };

        const responseData = await new Promise((resolve) => {
            rewardsApi.getParticipationChangeFlag(
                (data) => resolve(handleSuccess(data)),
                (error) => resolve(handleError(error))
            );
        });
        return responseData;
    };

    useEffect(() => {
        var location = window.location.pathname;

        if (location !== "/sessionExpired" && location !== "/error" && location !== "/rewardsOffline") {
            getUserInfo();
        } else {
            markGtmSessionUnavailable();
        }

        async function redirectToEcams() {
            const handleError = (error) => {
                logTrace(error, "error when calling rewardsApi.getEcamsUrl", "redirectToEcams in UserContextProvider.jsx");
            };

            const handleSuccess = (responseData) => {
                var ecamsUrl = responseData;
                window.location.href = ecamsUrl;
            };

            await rewardsApi.getEcamsUrl(handleSuccess, handleError);
        }

        function getDocuSignEventFromQuery(location) {
            if (location === "/") {
                let query = new URLSearchParams(window.location.search);
                return query.get(DOCUSIGN_EVENT);
            }
            return null;
        }

        function isProducerRole(role) {
            return role === "IA Primary Agent" || role === "IA Producer";
        }

        function shouldAllowAccess(participationIndicator, participationChangeOverride) {
            return participationIndicator || participationChangeOverride;
        }

        function shouldDenyProducerAccess(isProducer, participationIndicator, participationLevel, participationChangeOverride) {
            return isProducer && 
                   participationIndicator && 
                   participationLevel === "AGY" && 
                   !participationChangeOverride;
        }

        function handleDocuSignEventHome(eventType) {
            if (eventType && (eventType === DOCUSIGN_SIGNING_COMPLETE || eventType === DOCUSIGN_DECLINE)) {
                navigate("/home/?event=" + eventType);
            } else if (eventType && (eventType !== DOCUSIGN_SIGNING_COMPLETE && eventType !== DOCUSIGN_DECLINE)) {
                navigate("/error");
            } else {
                navigate(buildPathWithIncentiveId("/home", getIncentiveIdFromQuery()));
            }
        }

        function handleDocuSignEventOnboarding(eventType, hasCompleteOnboarding) {
            if (eventType && (eventType === DOCUSIGN_SIGNING_COMPLETE || eventType === DOCUSIGN_DECLINE)) {
                navigate("/onboarding/?event=" + eventType);
            } else if (eventType && (eventType !== DOCUSIGN_SIGNING_COMPLETE && eventType !== DOCUSIGN_DECLINE)) {
                navigate("/error");
            } else if (!hasCompleteOnboarding) {
                navigate("/onboarding");
            }
        }

        function navigateBasedOnOnboardingStatus(responseData, eventType, location) {
            const hasCompleteOnboarding = responseData.enrollmentStatus.taxInfo && responseData.enrollmentStatus.bankingOnFile;

           if (location === "/" && hasCompleteOnboarding) {
               handleDocuSignEventHome(eventType);
            } else {
               handleDocuSignEventOnboarding(eventType, hasCompleteOnboarding);
            }
        }

        async function processUserAccess(responseData, participationChangeFlag, location) {
            
            if (location === "/taxForm" || location === "/bankingDetails" || 
                location === "/error" || location === "/accessDenied") {
                return;
            }

            let eventType = getDocuSignEventFromQuery(location);
            const isProducer = isProducerRole(responseData.role);
            const { participationLevel, participationIndicator } = responseData.enrollmentStatus;

            const participationChangeOverride = participationChangeFlag &&
                parseFloat(responseData.numActiveIncentives) > 0;

            if (!shouldAllowAccess(participationIndicator, participationChangeOverride)) {
                navigate("/accessDenied");
                return;
            }

            if (shouldDenyProducerAccess(isProducer, participationIndicator, participationLevel, participationChangeOverride)) {
                navigate("/accessDenied");
                return;
            }

            navigateBasedOnOnboardingStatus(responseData, eventType, location);
        }

        async function getUserInfo() {
            const handleError = (error) => {
                logTrace(error, "error when calling rewardsApi.getUserInfo", "getUserInfo in UserContextProvider.jsx");
                markGtmSessionUnavailable();
                var location = window.location.pathname;

                if (location !== "/login" && location !== "/logout") {
                    redirectToEcams();
                }
            };

            const handleSuccess = async (responseData) => {
                const location = window.location.pathname;

                if (responseData.loginRequired) {
                    markGtmSessionUnavailable();
                    if (location !== "/login" && location !== "/logout") {
                        redirectToEcams();
                    }
                    return;
                }

                if (responseData.accessDenied) {
                    markGtmSessionUnavailable();
                    window.location.href = "/rewardsOffline";
                    return;
                }

                if (responseData.sessionExpired) {
                    markGtmSessionUnavailable();
                    navigate("/sessionExpired", { state: { redirectUrl: responseData.redirectUrl } });
                    return;
                }

                setUser(responseData);
                setGtmSession({
                    sessionId: responseData.sessionId,
                    agentId: responseData.agentId,
                    agencyId: responseData.agencyId,
                });
                const participationChangeFlag = getParticipationChangeFlag();

                processUserAccess(responseData, participationChangeFlag, location);
            };

            await rewardsApi.getUserInfo(handleSuccess, handleError);
        }
    }, [navigate]); 

    return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

export default UserContextProvider;
